import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { EVINCED_SEVERITIES, FormatHelper } from '@evinced-private/ui-common';

import {
	BaseScanViewV2,
	ScanTableDefinition
} from '../../../../components/scan-analysis/base/BaseScanViewV2';
import { MAX_RESULTS } from '../../../../consts/Results';
import { buildDeduplicatedSeveritySummary } from '../../../../helpers/SeverityLabelHelper';
import { useGetProperty } from '../../../../hooks/queries/useGetProperty';
import { IIssuesPaginationResponse } from '../../../../interfaces/IssuesPaginationResponse';
import { SummaryItem } from '../../../../interfaces/SummaryItem';
import { getAllIssuesSummary, getScanIssuesV2 } from '../../../../services/scan/ScanService';
import { FilterParams } from '../../../../types/FilterParams';
import { BreakdownSummary } from '../../../../types/OverviewTypes';
import { PaginationParams } from '../../../../types/PaginationParams';

import { getDefaultSort, getTableDefinition } from './ScanBreakdownTableHelperV2';

interface IScanBreakdownView {
	viewId: string;
	scanId: string;
	propertyId: string;
}

const defaultTableSort = getDefaultSort();

const ScanBreakdownView: FC<IScanBreakdownView> = ({
	viewId,
	scanId,
	propertyId
}: IScanBreakdownView) => {
	const [totalCount, setTotalCount] = useState<number>(null);
	const [scanResults, setScanResults] = useState<IIssuesPaginationResponse>(null);
	const [summaryPaneInfo, setSummaryPaneInfo] = useState<BreakdownSummary>();
	const screenId = `scan-${scanId}-view-${viewId}-breakdown-view`;
	const { data: property } = useGetProperty(propertyId);
	const customSeverityNames = useMemo(
		() => property?.scanConfiguration?.customSeverityNames ?? {},
		[property]
	);

	const loadTableItems = useCallback(
		async (
			scanId: string,
			paginationParams: PaginationParams,
			filtersParams?: FilterParams
		): Promise<void> => {
			const response: IIssuesPaginationResponse = await getScanIssuesV2(
				scanId,
				paginationParams,
				filtersParams,
				viewId
			);
			setScanResults(response);
		},
		[viewId]
	);

	useEffect(() => {
		if (scanResults?.overallTotal) {
			setTotalCount(scanResults.overallTotal);
		}
	}, [scanResults?.overallTotal]);

	const loadSummaryData = useCallback(async (): Promise<void> => {
		const summaryPaneInfo: BreakdownSummary = await getAllIssuesSummary(
			scanId,
			viewId,
			customSeverityNames
		);
		setSummaryPaneInfo(summaryPaneInfo);
	}, [scanId, viewId, customSeverityNames]);

	useEffect(() => {
		loadSummaryData();
	}, [loadSummaryData]);

	const severityEntries = useMemo(
		() =>
			buildDeduplicatedSeveritySummary(
				summaryPaneInfo?.severities,
				[EVINCED_SEVERITIES.CRITICAL, EVINCED_SEVERITIES.SERIOUS],
				customSeverityNames
			),
		[summaryPaneInfo?.severities, customSeverityNames]
	);

	const summaryPaneData = useMemo((): SummaryItem[] => {
		if (!summaryPaneInfo) {
			return [];
		}
		const { pages, issues, components } = summaryPaneInfo;
		return [
			{
				value: FormatHelper.formatNumber(pages),
				label: 'Pages'
			},
			{
				value: FormatHelper.formatNumber(issues),
				label: 'Total issues'
			},
			...severityEntries.map((entry) => ({
				value: FormatHelper.formatNumber(entry.value),
				label: `${entry.label} issues`
			})),
			{
				value: FormatHelper.formatNumber(components),
				label: 'Components'
			}
		];
	}, [summaryPaneInfo, severityEntries]);

	const tableDefinition = useMemo((): ScanTableDefinition => {
		if (!scanResults) {
			return null;
		}

		const { issues } = scanResults;
		const totalPaginationableResults =
			scanResults.overallTotal > MAX_RESULTS ? MAX_RESULTS : scanResults.overallTotal;
		return getTableDefinition(issues, totalCount, totalPaginationableResults);
	}, [scanResults, totalCount]);

	return (
		<BaseScanViewV2
			viewId={viewId}
			scanId={scanId}
			screenId={screenId}
			summaryPaneItems={summaryPaneData}
			tableDefinition={tableDefinition}
			defaultTableSort={defaultTableSort}
			loadSummaryData={loadSummaryData}
			loadTableItems={loadTableItems}
			propertyId={propertyId}
			renderCsvExportButton={true}
			tableWithUrlSwitcher={true}
		/>
	);
};

export { ScanBreakdownView };
